/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notifyError, notifyInfo, notifySuccess } from "redux/actions/Notifications";
import { Calendar as BigCalendar } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Event from "./Event";
import Grid from "@material-ui/core/Grid";
import { ConfirmDialog } from "components/common_components/Dialogs";
import AppContext from "AppContext";
import { hasElements } from "utils/arrays";
import { buildSetGoingSuccessMessage, calculateRange, colorOpacities, eventColors, getDefaultEventColor } from "./utils";
import Page from "components/common_components/Page/Page";
import Toolbar from "./components/Toolbar";
import Week from "./components/Week";
import CalendarContext from "./CalendarContext";
import Agenda from "./components/Agenda";
import { Paper } from "@material-ui/core";
import useStyles from "./useStyles";
import Header from "components/common_components/Header";
import { getCalendarLocalizer, isDate, now } from "utils/dateUtils";

const localizer = getCalendarLocalizer();
const DnDCalendar = withDragAndDrop(BigCalendar);

const Calendar = () => {
    const classes = useStyles();
    const { user } = useSelector((state) => state.Login);
    const DEFAULT_VIEW = "week";
    const DEFAULT_DATE = now();
    const DEFAULT_EVENT_COLOR = getDefaultEventColor();
    const [view, setView] = useState(DEFAULT_VIEW);
    const [date, setDate] = useState(DEFAULT_DATE);
    const [resources, setResources] = useState({ users: [] });
    const [eventData, setEventData] = useState(undefined);
    const [openEvent, setOpenEvent] = useState(false);
    const [eventMode, setEventMode] = useState(undefined);
    const [eventList, setEventList] = useState([]);
    const [guestCalendars, setGuestCalendars] = useState([]);
    const [cancelConfirmDialogProps, setCancelConfirmDialogProps] = useState({
        open: false,
        title: "Discard",
        content: "Do you want to discard this event's changes?",
        onSecondaryClick: () => {},
        onPrimaryClick: () => {},
        onClose: () => {
            setCancelConfirmDialogProps((prev) => ({
                ...prev,
                open: false,
            }));
        },
    });
    const dispatch = useDispatch();
    const { filters } = useContext(CalendarContext);
    const {
        api: {
            Calendar: { Events, Guests, Notifications },
            Settings: { Users },
        },
    } = useContext(AppContext);

    function getUsers() {
        Users.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    users: data,
                }));
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    users: [],
                }));
                dispatch(notifyError(error));
            },
        });
    }

    const getMyEventsAsync = async (range, userEmail) => {
        const { start, end } = range || calculateRange(view, date);
        if (isDate(start) && isDate(end)) {
            const res = await Events.getMyEventsAsync({
                data: {
                    email: userEmail || user.email,
                    start: start.getTime(),
                    end: end.getTime(),
                    ...filters,
                },
            });
            if (res && hasElements(res)) {
                return res;
            }
            return [];
        }
        return [];
    };

    useEffect(() => {
        const getMyEvents = async () => {
            const res = await getMyEventsAsync();
            setEventList(res);
        };
        getMyEvents();
        getUsers();

        return () => {
            setEventList([]);
            setResources((prev) => ({ ...prev, users: [] }));
        };
    }, []);

    useEffect(() => {
        if (view && date) {
            const getMyEvents = async () => {
                const res = await getMyEventsAsync(calculateRange(view, date));
                setEventList(res);
            };
            getMyEvents();
        }
    }, [view, date, filters]);

    const restartEvent = (closeSideWindow = true) => {
        if (closeSideWindow) {
            setOpenEvent(false);
        }
        setEventMode(undefined);
        setEventData(undefined);
        setGuestCalendars([]);
    };

    const onCancelEventHandler = (primaryClickCallback, closeSideWindow = true) => {
        const executeCallback = () =>
            setTimeout(() => primaryClickCallback && typeof primaryClickCallback === "function" && primaryClickCallback(), 100);

        if (eventMode === "view") {
            restartEvent(closeSideWindow);
            executeCallback();
        } else {
            setCancelConfirmDialogProps((prev) => ({
                ...prev,
                open: true,
                onSecondaryClick: () => {
                    setCancelConfirmDialogProps((prev) => ({ ...prev, open: false }));
                },
                onPrimaryClick: () => {
                    restartEvent(closeSideWindow);
                    setCancelConfirmDialogProps((prev) => ({ ...prev, open: false }));
                    executeCallback();
                },
            }));
        }
    };

    const onEventMoved = (data) => {
        const { start, end, event } = data;

        const modifiedEvent = {
            ...event,
            startDateTime: start,
            endDateTime: end,
            guestEmails: hasElements(event.guests) && event.guests.map((g) => g.guestEmail),
        };

        Events.update({
            payload: modifiedEvent,
            onSuccess: () => {
                dispatch(notifySuccess(`Event updated successfully!`));
                onEventSaved();
            },
            onError: () => {
                dispatch(notifyError("Update event failed"));
            },
        });
    };

    const onEventResize = (data) => {
        onEventMoved(data);
    };

    const onEventDrop = (data) => {
        onEventMoved(data);
    };

    const generateNewEvent = (start, end) => {
        return {
            id: null,
            title: "",
            allDay: false,
            start,
            end,
            placeType: "none",
            place: "",
            description: "",
            guestPermissionsModify: false,
            guestPermissionsInviteOthers: true,
            guestPermissionsSeeGuestList: true,
            internalGuests: [],
            externalEmail_Temp: "",
            externalGuestEmails: undefined,
            eventColor: DEFAULT_EVENT_COLOR,
            notificationInterval: 10,
            notificationTimeUnit: "m",
        };
    };

    const initializeNewEvent = (data) => {
        const { start, end } = data;
        setEventMode("add");
        setEventData(generateNewEvent(start, end));
        setOpenEvent(true);
    };

    const onSelectSlot = (data) => {
        if (eventData) {
            onCancelEventHandler(() => initializeNewEvent(data), false);
        } else {
            initializeNewEvent(data);
        }
    };

    const onSelectEvent = (data) => {
        if (data.id) {
            setEventMode("view");
        } else {
            setEventMode("add");
        }
        setEventData(data);
        setOpenEvent(true);
    };

    const onNavigate = (newDate, view, action) => {
        setDate(newDate);
    };

    const onView = (view) => {
        setView(view);
    };

    const onEventChangeHandler = (eventData) => {
        setEventData(eventData);
    };

    const getEventsToShow = () => {
        let eventsToShow = eventList;
        if (eventData) {
            if (eventMode === "add") {
                eventsToShow = [...eventList, eventData];
            } else {
                const filteredEvents = eventList.filter((x) => x.id !== eventData.id);
                eventsToShow = [...filteredEvents, eventData];
            }
        }
        if (hasElements(guestCalendars) && ["add", "edit"].includes(eventMode)) {
            return guestCalendars.reduce((arr, item) => {
                return [...arr, ...item.events.map((event) => ({ ...event, eventColor: item.color }))];
            }, eventsToShow);
        }
        return eventsToShow;
    };

    const onEventSaved = async () => {
        const res = await getMyEventsAsync();
        setEventList(res);
        restartEvent();
    };

    const eventStyleGetter = (event, start, end, isSelected) => {
        const { eventColor, id, going } = event;
        const evtColor = eventColor || DEFAULT_EVENT_COLOR;
        const WHITE_COLOR = "white";
        const isNewEvent = !Boolean(id);

        const backgroundColor = isNewEvent ? evtColor : going ? evtColor : WHITE_COLOR;
        const borderColor = isNewEvent ? WHITE_COLOR : going ? (isSelected ? evtColor : WHITE_COLOR) : evtColor;
        const fontColor = isNewEvent ? WHITE_COLOR : going ? WHITE_COLOR : evtColor;

        const style = {
            backgroundColor,
            borderRadius: "5px",
            border: `1px solid ${borderColor}`,
            color: fontColor,
            display: "block",
            boxShadow:
                isNewEvent || isSelected
                    ? "0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%), 0px 3px 5px -1px rgb(0 0 0 / 20%)"
                    : "none",
            textDecoration: going === false && "line-through",
        };
        return {
            style,
        };
    };

    const onEditEventHandler = (data) => {
        setEventMode("edit");
        setEventData(data);
        setOpenEvent(true);
    };

    const onDeleteEventHandler = async (data) => {
        try {
            const res = await Events.deleteAsync({ data: { ids: [data.id] } });
            if (res && res.status) {
                restartEvent();
                dispatch(notifyInfo(`The event "${data.title}" has been deleted.`));
                const resp = await getMyEventsAsync();
                setEventList(resp);
            }
        } catch (message) {
            dispatch(notifyError(`The event couldn't be deleted. \n Cause: \n ${message}`));
        }
    };

    const onGoingHandler = async (calendarGuestId, answer, note) => {
        try {
            const res = await Guests.setGoingAsync({ calendarGuestId, answer, note });
            if (res && res.data === calendarGuestId) {
                restartEvent();
                const notifier = answer ? notifySuccess : notifyInfo;
                dispatch(notifier(buildSetGoingSuccessMessage(answer, note)));
                const resp = await getMyEventsAsync();
                setEventList(resp);
            }
        } catch (message) {
            dispatch(notifyError(`The answer couldn't be saved. \n Cause: \n ${message}`));
        }
    };

    const onNotifyHandler = async (guestId) => {
        const res = await Notifications.notifySingleReminder(guestId);
        if (res && Boolean(res.data)) {
            dispatch(notifySuccess("Notification sent to the guest!"));
        }
    };

    const getGuestCalendarColor = () => {
        const isColorAlreadyInUse = (color) => guestCalendars.some((gc) => gc.color === color);
        const nonDefaultColors = eventColors.filter((x) => !Boolean(x.default));
        const notInUseColors = nonDefaultColors.filter((x) => !isColorAlreadyInUse(x.value));
        if (hasElements(notInUseColors)) {
            return notInUseColors[0].value;
        }

        let notInUseColor = null;

        for (const opacity of colorOpacities) {
            for (const color of nonDefaultColors) {
                const newColor = `${color}${opacity}`;
                if (!isColorAlreadyInUse(newColor)) {
                    notInUseColor = newColor;
                    break;
                }
            }
        }

        return notInUseColor || `${DEFAULT_EVENT_COLOR}${colorOpacities[0]}`;
    };

    const getInternalGuestCalendar = async (guestUsers) => {
        if (hasElements(guestUsers)) {
            let newGuestCalendars = guestCalendars.filter((x) => guestUsers.some((g) => g.id === x.userId));
            for (const guestUser of guestUsers) {
                if (guestUser.id !== user.id) {
                    const res = await getMyEventsAsync(calculateRange(view, date), guestUser.email);
                    const existingCalendar = newGuestCalendars.find((x) => x.userId === guestUser.id);
                    if (existingCalendar) {
                        const updatedCalendar = { ...existingCalendar, events: res };
                        newGuestCalendars = [...newGuestCalendars.filter((x) => x.userId !== guestUser.id), updatedCalendar];
                    } else {
                        newGuestCalendars = [...newGuestCalendars, { userId: guestUser.id, events: res, color: getGuestCalendarColor() }];
                    }
                }
            }
            setGuestCalendars(newGuestCalendars);
        } else {
            setGuestCalendars([]);
        }
    };

    return (
        <Page>
            <Header title="Calendar" />
            <Grid container spacing={3} className={classes.container}>
                {openEvent && (
                    <Grid item xs={4} className="card">
                        <Event
                            data={eventData}
                            onChange={onEventChangeHandler}
                            mode={eventMode}
                            onCancel={onCancelEventHandler}
                            resources={resources}
                            onSuccessCallback={onEventSaved}
                            getInternalGuestCalendar={getInternalGuestCalendar}
                            viewModeActionHandlers={{
                                onEdit: onEditEventHandler,
                                onDelete: onDeleteEventHandler,
                                onClose: restartEvent,
                                onGoing: onGoingHandler,
                                onNotify: onNotifyHandler,
                            }}
                        />
                    </Grid>
                )}
                <Grid item xs={openEvent ? 8 : 12} className="card">
                    <Paper className={classes.paper} variant="elevation">
                        <DnDCalendar
                            defaultDate={DEFAULT_DATE}
                            defaultView={DEFAULT_VIEW}
                            views={["day", "week", "month", "agenda"]}
                            events={getEventsToShow()}
                            localizer={localizer}
                            onEventDrop={onEventDrop}
                            onEventResize={onEventResize}
                            onSelectSlot={onSelectSlot}
                            onSelectEvent={onSelectEvent}
                            onNavigate={onNavigate}
                            onView={onView}
                            resizable
                            selectable
                            style={{ height: "90vh" }}
                            eventPropGetter={eventStyleGetter}
                            components={{
                                toolbar: Toolbar,
                                week: {
                                    header: Week.Header,
                                },
                                agenda: {
                                    date: Agenda.Header,
                                },
                            }}
                        />
                    </Paper>
                </Grid>
                <ConfirmDialog {...cancelConfirmDialogProps} />
            </Grid>
        </Page>
    );
};

export default Calendar;
